'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

const SITE_URL = process.env.NEXT_PUBLIC_APP_URL ?? 'https://www.crewahead.com';

function formatSegment(segment: string) {
  return decodeURIComponent(segment)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function Breadcrumbs() {
  const pathname = usePathname();
  const segments = pathname.split('/').filter((segment) => segment && !segment.startsWith('('));

  if (segments.length === 0) {
    return null;
  }

  const crumbs = [
    { name: 'Home', href: '/' },
    ...segments.map((segment, index) => ({
      name: formatSegment(segment),
      href: `/${segments.slice(0, index + 1).join('/')}`,
    })),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.href}`,
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-6 pt-6 text-sm text-ash lg:px-8">
        <ol className="flex flex-wrap items-center gap-2">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;

            return (
              <li key={crumb.href} className="flex items-center gap-2">
                {index > 0 && <span aria-hidden="true">/</span>}
                {isLast ? (
                  <span aria-current="page" className="font-medium text-white">
                    {crumb.name}
                  </span>
                ) : (
                  <Link href={crumb.href} className="inline-flex min-h-11 min-w-11 items-center justify-center px-2 hover:text-neon-cyan">
                    {crumb.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
